import type { ContentTextDTO, ContentVersionDTO } from '@today/types'
import type {
	AddLanguageDialogViewProps,
	ApprovalTimelineViewProps,
	ApproveDialogViewProps,
	DiffViewProps,
	LanguageTabsViewProps,
	LifecycleBarViewProps,
	PublishDialogViewProps,
	ReasonDialogViewProps,
	TextHeaderViewProps,
	VersionsPanelViewProps,
} from '@today/ui-domain'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { orderedLocales } from './localization-rules'
import { useContentLocales, useContentT, useContentTypes } from './useContentQueries'
import { useAddLanguage, useDraft, useTabFacts } from './useLanguageTabs'
import { useLocalizationLifecycle } from './useLocalizationLifecycle'
import { useTextHeader } from './useTextHeader'
import { useVersionsPanel } from './useVersionsPanel'

export interface TextEditorProps {
	header: TextHeaderViewProps
	versions: VersionsPanelViewProps
	diff: DiffViewProps | null
	tabs: Omit<LanguageTabsViewProps, 'lifecycle'>
	lifecycle: LifecycleBarViewProps
	timeline: ApprovalTimelineViewProps | null
	approve: ApproveDialogViewProps
	reason: ReasonDialogViewProps
	publish: PublishDialogViewProps
	addLanguage: AddLanguageDialogViewProps
}

type Selection = { version?: number; locale?: string }

/** The selection kept in the URL: `version`, `locale` and the optional `compare` version. */
function useSelection(text: ContentTextDTO, versions: readonly ContentVersionDTO[]) {
	const [search, setSearch] = useSearchParams()
	const latest = versions.reduce((max, v) => Math.max(max, v.versionNumber), 0)
	const wanted = Number(search.get('version'))
	const version = versions.find((v) => v.versionNumber === wanted) ?? versions.find((v) => v.versionNumber === latest)
	const locales = orderedLocales(version?.localizations ?? [], text.sourceLocale)
	const locale = locales.find((l) => l.locale === search.get('locale'))?.locale ?? locales[0]?.locale ?? ''
	const compare = Number(search.get('compare'))
	const apply = (next: Selection) => {
		const params = new URLSearchParams(search)
		params.delete('compare')
		if (next.version !== undefined) params.set('version', String(next.version))
		if (next.locale !== undefined) params.set('locale', next.locale)
		else if (next.version !== undefined) params.delete('locale')
		setSearch(params)
	}
	return {
		version,
		locale,
		summaryId: version?.localizations.find((l) => l.locale === locale)?.id,
		initialCompare:
			compare && version && compare !== version.versionNumber
				? { locale, from: Math.min(compare, version.versionNumber), to: Math.max(compare, version.versionNumber) }
				: null,
		apply,
	}
}

/** The URL selection and the draft, with a guard that asks before a dirty draft is left. */
function useGuardedSelection(text: ContentTextDTO, versions: readonly ContentVersionDTO[]) {
	const selection = useSelection(text, versions)
	const draft = useDraft(text.id, selection.summaryId)
	const [pending, setPending] = useState<Selection | null>(null)
	const request = (next: Selection) => (draft.dirty ? setPending(next) : selection.apply(next))
	return { selection, draft, pending, setPending, request }
}

/** The lifecycle bar: save only on an editable draft; the draft's save state joins the bar's. */
const barProps = (
	bar: ReturnType<typeof useLocalizationLifecycle>['bar'],
	canSave: boolean,
	draft: ReturnType<typeof useDraft>,
): LifecycleBarViewProps => ({
	...bar,
	canSave,
	isBusy: bar.isBusy || draft.isSaving,
	error: draft.formError ?? bar.error,
})

/** The draft, selection and discard-guard part of the language tabs. */
function tabsState(
	selection: ReturnType<typeof useSelection>,
	draft: ReturnType<typeof useDraft>,
	pending: Selection | null,
	setPending: (next: Selection | null) => void,
	request: (next: Selection) => void,
) {
	return {
		selected: selection.locale,
		draft: draft.localization ? draft.draft : { title: '', body: '' },
		errors: draft.errors,
		discardOpen: pending !== null,
		onSelect: (locale: string) => request({ locale }),
		onChange: draft.change,
		onStay: () => setPending(null),
		onDiscard: () => {
			draft.reset()
			if (pending) selection.apply(pending)
			setPending(null)
		},
	}
}

/**
 * `/content/texts/:id` (TO-420 content-admin-ui REQ-003…REQ-007): header,
 * versions, language tabs with the draft editor, lifecycle, track and diff.
 * A tab or version switch from a dirty draft asks before discarding it.
 */
export function useTextEditor(text: ContentTextDTO, versions: readonly ContentVersionDTO[]): TextEditorProps {
	const { t } = useContentT()
	const types = useContentTypes()
	const type = types.data?.find((x) => x.code === text.typeCode)
	const { selection, draft, pending, setPending, request } = useGuardedSelection(text, versions)
	const { locales } = useContentLocales(type?.requiredLocales)
	const panel = useVersionsPanel({
		text,
		versions,
		selected: { versionNumber: selection.version?.versionNumber ?? 0, locale: selection.locale },
		initialCompare: selection.initialCompare,
		onSelect: (version) => request({ version }),
	})
	const onAdded = (locale: string) => selection.apply({ locale })
	const addLanguage = useAddLanguage({ text, version: selection.version, locales, onAdded })
	const lifecycle = useLocalizationLifecycle({
		text,
		type,
		versions,
		localization: draft.localization,
		dirty: draft.dirty,
		onSave: draft.save,
		onNewVersion: () => panel.openNewVersion(selection.version?.versionNumber ?? 0),
	})
	const facts = useTabFacts(text, selection.version, versions, draft.localization)
	const { open: openAddLanguage, ...addLanguageProps } = addLanguage
	return {
		header: useTextHeader(text, type?.name ?? text.typeCode),
		versions: panel.panel,
		diff: panel.diff,
		tabs: {
			t,
			...facts,
			...tabsState(selection, draft, pending, setPending, request),
			onAddLanguage: facts.canAdd ? openAddLanguage : undefined,
		},
		lifecycle: barProps(lifecycle.bar, facts.editable, draft),
		timeline: draft.localization ? lifecycle.timeline : null,
		approve: lifecycle.approve,
		reason: lifecycle.reason,
		publish: lifecycle.publish,
		addLanguage: addLanguageProps,
	}
}
